import React from 'react';
import { Box, Typography } from '@mui/material';
import { useLocale } from '../contexts/LocaleContext';
import type { ComboRebateOffer } from './ProductPricingBadge';

const SOLD_WITH_ALREADY =
  /\bsold\s+w(?:ith)?\/?\s+|\bwhen\s+(?:purchased|bought)\s+with\b/i;

function formatComboCondition(condition?: string): string {
  const text = String(condition || '').trim();
  if (!text) return '';
  if (SOLD_WITH_ALREADY.test(text)) return ` ${text}`;
  return ` when sold with ${text}`;
}

export const ComboRebateOffersNote: React.FC<{
  offers?: ComboRebateOffer[];
  compact?: boolean;
}> = ({ offers, compact = false }) => {
  const { formatCurrency, formatDate } = useLocale();
  const live = (offers || []).filter(
    (offer) => offer.instantRebate > 0 && offer.status !== 'expired'
  );
  if (live.length === 0) return null;

  const formatDay = (value?: Date | string | null): string | null => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return formatDate(date, { month: 'short', day: 'numeric' }) || null;
  };

  return (
    <Box
      sx={{
        mt: compact ? 0.5 : 1,
        px: compact ? 1 : 1.5,
        py: compact ? 0.5 : 1,
        borderRadius: 1,
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}
      >
        Combo rebate{live.length > 1 ? 's' : ''} available (does not change this item's price)
      </Typography>
      {live.map((offer) => {
        const when =
          offer.status === 'upcoming' && formatDay(offer.effectiveDate)
            ? `from ${formatDay(offer.effectiveDate)}`
            : formatDay(offer.endDate)
              ? `through ${formatDay(offer.endDate)}`
              : null;
        return (
          <Typography
            key={`${offer.condition}-${offer.instantRebate}-${offer.status}`}
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.35 }}
          >
            {formatCurrency(offer.instantRebate)} IR{formatComboCondition(offer.condition)}
            {when ? ` ${when}` : ''}
            {offer.status === 'upcoming' ? ' (upcoming)' : ''}
          </Typography>
        );
      })}
    </Box>
  );
};
